<template>
  <button
    :class="[
      { 'bg-color-theme text-white': initialFilter || currentFilter },
      { 'text-common-grey bg-white': !(initialFilter || currentFilter) }
    ]"
    class="inline-flex whitespace-nowrap items-center justify-center px-5 py-[7px] border border-gray-300 font-normal rounded-md shadow-sm dark:border-dark-divider-border dark:bg-dark-box-background focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue"
    @click="openFilter">
    <ListFilter
      :color="initialFilter || currentFilter ? 'white' : '#899399'"
      class="w-5 h-5 mr-3 group-hover:text-gray-500"
      aria-hidden="true" />
    {{ filter_label }}
  </button>

  <Modal v-model:is-open="filter_open" max-width="max-w-[900px] mx-5" :use-toast="true" :default-success-msg="$t('Filter applied')">
    <template #form="{ done: showIsDone }">
      <div :class="{ 'opacity-60': readOnly }">
        <div class="bg-gradient-to-b from-[#B0D0FF]/70 h-36 w-[100%] absolute left-0 top-0 pointer-events-none" />
        <div class="text-black flex relative z-10 mt-4 items-center space-x-4">
          <ListFilter color="black" class="w-8 h-8 text-black" />
          <span class="text-2xl">{{ selected_group_filter_label }}</span>
        </div>
        <div class="text-common-grey relative z-10 mt-1">
          {{ $t("Search records from this table, save the search criteria and use them in the whole platform.") }}
        </div>

        <!-- AI Filter Generation Section -->
        <div v-if="!readOnly" class="relative z-10 mt-4 p-4 bg-white border border-gray-200 rounded-md shadow-sm">
          <div class="flex items-center mb-2">
            <Sparkles class="w-5 h-5 text-blue mr-2" />
            <h3 class="text-md font-medium">
              {{ $t("Generate Filter with AI") }}
            </h3>
          </div>
          <div class="flex gap-x-2">
            <TextInput
              v-model="ai_prompt"
              name="ai-prompt"
              :classes="'w-full'"
              :placeholder="$t('Describe your filter in natural language...')"
              :validate-on-input="true"
              :show-label="false"
              :disabled="isGeneratingFilter" />
            <button
              type="button"
              class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue rounded-md hover:bg-blue-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="!ai_prompt || isGeneratingFilter"
              @click="generateAIFilter">
              <Sparkles v-if="!isGeneratingFilter" class="w-4 h-4 mr-2" />
              <svg v-else
                   class="animate-spin h-4 w-4 mr-2 text-white"
                   xmlns="http://www.w3.org/2000/svg"
                   fill="none"
                   viewBox="0 0 24 24">
                <circle class="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        stroke-width="4" />
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              {{ isGeneratingFilter ? $t("Generating...") : $t("Generate") }}
            </button>
          </div>
          <div v-if="generationError" class="mt-2 text-sm text-red-600">
            {{ generationError }}
          </div>
          <div class="mt-1 text-xs text-gray-500">
            {{ $t("Example: \"Companies with more than 100 employees in the healthcare industry\"") }}
          </div>
        </div>
        <AdvancedMetricsFilterForm
          v-model:current-filter="current_filter"
          :read-only="readOnly"
          :initial-filter="initialFilter"
          :disabled="disabled"
          :is-box-background="isBoxBackground"
          :object-type="objectType"
          :selected-group="selectedGroup"
          @update="handleUpdateFilter" />

        <div class="text-center flex justify-between w-full mt-12">
          <div>
            <button class="text-dark-brand-blue" @click="filter_open = false">
              {{ readOnly ? $t("Close") : $t("Cancel") }}
            </button>
          </div>
          <div :class="{ 'pointer-events-none opacity-0': readOnly }" class="flex space-x-2">
            <button
              type="button"
              class="btn cancel-btn w-fit ml-auto"
              @click="
                () => {
                  handleOpenGroupClick()
                }
              ">
              <ChevronDown
                :class="{
                  'rotate-180': group_form_expanded || group_form_visible
                }"
                class="w-5 h-5 text-gray-400 mr-3 transition-all duration-500 ease-in-out"
                aria-hidden="true" />
              {{ selectedGroup ? $t("Update") : $t("Save as") }} {{ $t("group") }}
            </button>
            <button type="button" class="btn primary-btn w-fit" @click="emitFilter(showIsDone)">
              {{ $t("Show results") }}
            </button>
          </div>
        </div>

        <div
          :class="[{ 'h-[360px]': group_form_expanded }, { 'opacity-100': group_form_visible }]"
          class="h-0 opacity-0 transition-all ease-in-out duration-200">
          <NewGroupForm
            v-if="group_form_visible"
            :hide-filters="true"
            :selected-group="selectedGroup"
            :filter="transformMetricFilterToServerFilter(filter).filter"
            :object-type="objectType"
            @done="handleGroupCreated"
            @cancel="handleOpenGroupClick()" />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script>
import NewGroupForm from "@/components/forms/NewGroupForm.vue"
import Modal from "@/components/shared/Modal.vue"
import TextInput from "@/components/shared/TextInput.vue"

import AdvancedMetricsFilterForm from "@/components/forms/AdvancedMetricsFilterForm.vue"
import { ListFilter, Sparkles } from "lucide-vue-next"
import { ChevronDown } from "lucide-vue-next"
import { transformMetricFilterToServerFilter } from "@/services/data.transformers"
import { generateFilter } from "@/services/ai.service"

export default {
  components: {
    AdvancedMetricsFilterForm,
    NewGroupForm,
    ListFilter,
    Modal,
    ChevronDown,
    TextInput,
    Sparkles
  },

  props: {
    readOnly: null,
    label: null,
    currentFilter: null,

    initialFilter: {
      type: String,
      default: ""
    },

    disabled: {
      type: Boolean,
      default: false
    },
    isBoxBackground: {
      type: Boolean,
      default: false
    },
    objectType: {
      type: String,
      required: false
    },
    selectedGroup: {
      type: Object,
      required: false
    }
  },
  emits: ["reset", "cancel", "filter", "change", "update:selectedGroup"],

  data() {
    return {
      transformMetricFilterToServerFilter,
      filter: [],
      filter_open: false,
      group_form_expanded: false,
      group_form_visible: false,
      ai_prompt: "",
      isGeneratingFilter: false,
      generationError: null,
      current_filter: this.currentFilter,
    }
  },
  computed: {
    selected_group_filter_label() {
      return this.selectedGroup
        ? this.$t("Group Filter") + ` ${this.readOnly ? "(" + this.$t("read only") + ")" : ""}`
        : this.$t("Filter table")
    },
    filter_label() {
      return this.label
        ? this.label
        : this.selectedGroup
          ? this.readOnly
            ? this.$t("View applied group filter")
            : this.$t("Update applied group filter")
          : this.initialFilter || this.currentFilter
            ? this.$t("Preview table filter")
            : this.$t("Filter table")
    }
  },
  watch: {},
  created() {
    // this.init()
  },

  methods: {
    async generateAIFilter() {
      if (!this.ai_prompt || !this.objectType) {return}
      
      this.isGeneratingFilter = true
      this.generationError = null
      
      try {
        const result = await generateFilter(this.ai_prompt, { object_type: this.objectType })
        
        if (result && result.expression) {
          const regex = /(\()\s*|\s*(\))/g
          // eslint-disable-next-line no-unused-vars
          const cleanedExpression = result.expression.replace(regex, (_, open, close) => open ? "( " : " )")
          this.current_filter = cleanedExpression
          this.filter = this.current_filter
          this.$emit("filter", this.current_filter)
        }
      } catch (error) {
        console.error("Error generating filter:", error)
        this.generationError = this.$t("Failed to generate filter. Please try again or refine your prompt.")
      } finally {
        this.isGeneratingFilter = false
        this.ai_prompt = ""
      }
    },
    
    handleUpdateFilter(filter) {
      this.filter = filter
    },
    handleGroupCreated(group) {
      this.group_form_visible = false
      setTimeout(() => {
        this.group_form_expanded = false
      }, 300)
      this.$emit("update:selectedGroup", group)
    },
    handleOpenGroupClick() {
      if (this.group_form_expanded) {
        this.group_form_visible = false
        setTimeout(() => {
          this.group_form_expanded = false
        }, 300)
      } else {
        this.group_form_expanded = true
        setTimeout(() => {
          this.group_form_visible = true
        }, 300)
      }
    },

    openFilter() {
      this.filter_open = true
    },

    emitFilter(callback) {
      const filter_cleaned = []
      this.filter.forEach((item) => {
        if (item.data_type == "String") {
          if (Array.isArray(item.value)) {
            item.value = String(item.value)
          }
          // let value = item.value.replace(/[']+/g, "")
          filter_cleaned.push({
            ...item,
            value: item.value == null ? "" : item.value.trim()
          })
        } else {
          if (
            item.value == null ||
            item.value == undefined ||
            item.value == "" ||
            item.value == "null" ||
            item.value == "undefined" ||
            item.value == "None"
          ) {
            item.value = null
          } else if (
            (item.data_type == "Date" || item.data_type == "DateTime") &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DD")
          } else if (
            item.data_type == "DateTime" &&
            !(
              item.operator.value == "__DAYS_WITHIN_LAST_X_DAYS__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_AGO__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_AGO__" ||
              item.operator.value == "__DAYS_MORE_THAN_X_DAYS_FROM_NOW__" ||
              item.operator.value == "__DAYS_WITHIN_NEXT_X_DAYS__" ||
              item.operator.value == "__DAYS_EXACT_DAYS_FROM_NOW__"
            )
          ) {
            item.value = this.$moment(item.value).format("YYYY-MM-DDTHH:mm:ss")
          }
          filter_cleaned.push({ ...item, value: item.value })
        }
      })
      let parsed_filter = transformMetricFilterToServerFilter([...filter_cleaned])

      callback()
      if (parsed_filter.valid) {
        this.$emit("filter", parsed_filter.filter)
      } else {
        this.$emit("filter", "")
      }
    }
  }
}
</script>
<style scoped lang="postcss">
.ghost {
  @apply bg-gray-300 border-0 w-full;
}

.chosen {
  @apply bg-transparent border-0;
}

.chosen .line {
  opacity: 0;
}
</style>
